import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Flame, Award, BookOpen, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button.jsx';
import ProgressCircle from '../components/ProgressCircle';
import MyBlogPosts from '../components/MyBlogPosts';
import EnrollBlock from '../components/EnrollBlock';
import EditableText from '../components/EditableText';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { authUrlWithNext } from '../lib/whatsapp';

const Dashboard = () => {
  const { user, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      navigate(authUrlWithNext(location));
    }
  }, [user, loading, navigate, location]);

  const handleRecordPractice = async () => {
    try {
      const result = await api.recordPractice();
      toast.success(`Practice recorded! Streak: ${result.streak_days} days`);
      await refreshUser();
    } catch (error) {
      toast.error('Failed to record practice');
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Gate: if the user is signed in but has not enrolled in any course or
  // live class, show a friendly "practice awaits" prompt with the shared
  // two-option enroll block in place of the dashboard content (Change 8).
  const isEnrolled = (user.enrolled_levels?.length || 0) > 0 || !!user.live_class_enrolled;
  if (!isEnrolled) {
    return (
      <div className="min-h-screen py-32 noise-texture" data-testid="dashboard-enroll-gate">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <h1 className="text-3xl md:text-5xl font-heading font-semibold mb-4">
              <EditableText id="dashboard.gate.heading" defaultValue="Practice awaits" />
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              <EditableText
                id="dashboard.gate.body"
                defaultValue="The dashboard is available once you have enrolled. Choose how you would like to begin below."
                multiline
              />
            </p>
          </motion.div>
          <EnrollBlock onSelfPacedClick={() => navigate('/courses/1')} />
        </div>
      </div>
    );
  }

  const progressPercentage = (user.completed_levels.length / 4) * 100;

  const badges = [
    { id: 'sthira', name: 'Sthira', description: 'Steady in Practice · Grahita Shakti', icon: '🌱' },
    { id: 'drashta', name: 'Drashta', description: 'The Witness · Nimeṣa', icon: '👁' },
    { id: 'spanda_sadhaka', name: 'Spanda Sadhaka', description: 'In Living Awareness · Unmeṣa', icon: '🔥' },
    { id: 'siddha_chitta', name: 'Siddha Chitta', description: 'A Refined Mind · Pratyabhijñā', icon: '⚡' },
  ];

  const STAGE_NAMES = { 1: 'Grahita Shakti', 2: 'Nimeṣa', 3: 'Unmeṣa', 4: 'Pratyabhijñā' };

  return (
    <div className="min-h-screen py-32 noise-texture">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-heading font-semibold mb-2">
              Welcome back, {user.name}
            </h1>
            <p className="text-muted-foreground">Continue your journey to inner mastery</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
            <div className="lg:col-span-2 bg-card border border-border/60 rounded-2xl p-8">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-heading font-normal mb-2">Your Progress</h2>
                  <p className="text-muted-foreground">
                    Grahita Shakti · Foundation Programme
                  </p>
                </div>
                <div className="mt-4 md:mt-0">
                  <ProgressCircle progress={progressPercentage} size={140} strokeWidth={10} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="p-4 rounded-xl bg-muted/50">
                  <div className="flex items-center gap-2 text-secondary mb-2">
                    <Flame size={20} />
                    <span className="text-sm font-medium">Streak</span>
                  </div>
                  <div className="text-3xl font-heading font-semibold">{user.streak_days}</div>
                  <div className="text-xs text-muted-foreground mt-1">consecutive days</div>
                </div>

                <div className="p-4 rounded-xl bg-muted/50">
                  <div className="flex items-center gap-2 text-accent mb-2">
                    <BookOpen size={20} />
                    <span className="text-sm font-medium">Practices</span>
                  </div>
                  <div className="text-3xl font-heading font-semibold">{user.total_practices}</div>
                  <div className="text-xs text-muted-foreground mt-1">total sessions</div>
                </div>
              </div>

              <Button
                onClick={handleRecordPractice}
                className="w-full rounded-full py-6 text-lg"
                data-testid="record-practice-button"
              >
                Complete Today's Sadhana
              </Button>
            </div>

            <div className="bg-card border border-border/60 rounded-2xl p-8">
              <div className="flex items-center gap-2 mb-6">
                <Award className="text-secondary" size={24} />
                <h2 className="text-xl font-heading font-normal">Your Badges</h2>
              </div>

              <div className="space-y-4">
                {badges.map((badge) => {
                  const earned = user.badges.includes(badge.id);
                  return (
                    <div
                      key={badge.id}
                      className={`p-4 rounded-xl border transition-all ${
                        earned
                          ? 'border-secondary/30 bg-secondary/5'
                          : 'border-border/30 bg-muted/20 opacity-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">{badge.icon}</div>
                        <div className="flex-1">
                          <div className="font-medium text-sm mb-1">{badge.name}</div>
                          <div className="text-xs text-muted-foreground">{badge.description}</div>
                        </div>
                        {earned && (
                          <div className="text-secondary text-xs font-medium">✓</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="bg-card border border-border/60 rounded-2xl p-8">
            <h2 className="text-2xl font-heading font-normal mb-6">Course Progress</h2>
            <div className="space-y-4">
              {[1, 2, 3, 4].map((level) => {
                const completed = user.completed_levels.includes(level);
                const current = user.current_level === level;
                const locked = level > user.current_level;

                // Determine status text
                let statusText = 'Locked';
                if (completed) statusText = 'Completed';
                else if (current) statusText = 'In Progress';

                // Determine container classes
                let containerClasses = 'border-border/30 bg-muted/20';
                if (completed) containerClasses = 'border-secondary/30 bg-secondary/5';
                else if (current) containerClasses = 'border-primary/30 bg-primary/5';

                // Determine badge classes
                let badgeClasses = 'bg-muted text-muted-foreground';
                if (completed) badgeClasses = 'bg-secondary text-secondary-foreground';
                else if (current) badgeClasses = 'bg-primary text-primary-foreground';

                return (
                  <div
                    key={level}
                    className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${containerClasses}`}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${badgeClasses}`}
                    >
                      {completed ? '✓' : level}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{STAGE_NAMES[level]}</div>
                      <div className="text-sm text-muted-foreground">
                        {statusText}
                      </div>
                    </div>
                    {current && (
                      <Button
                        onClick={() => navigate(`/courses/${level}`)}
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        data-testid={`view-level-${level}-button`}
                      >
                        Continue
                      </Button>
                    )}
                    {!locked && !current && (
                      <Button
                        onClick={() => navigate(`/courses/${level}`)}
                        variant="ghost"
                        size="sm"
                        className="rounded-full"
                      >
                        View
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <MyBlogPosts />
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;